package app.footyguide.web.components.club

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import app.footyguide.core.locale.AppStrings
import app.footyguide.core.theme.Club
import app.footyguide.core.theme.ClubCatalog
import app.footyguide.domain.model.Game
import app.footyguide.web.components.ChannelBadgesRow
import app.footyguide.web.components.CrestSizes
import app.footyguide.web.components.Icons
import app.footyguide.web.components.SafeImg
import app.footyguide.web.components.formatBannerDate
import app.footyguide.web.page.extractKickoffTime
import app.footyguide.web.route.WebRoutes

private fun canonicalClubSlug(raw: String): String =
    ClubCatalog.bySlug(raw)?.slug ?: raw.trim().lowercase()

fun formatTeamDisplayName(teamId: String): String {
    ClubCatalog.bySlug(teamId)?.let { return it.name }
    return teamId
        .trim()
        .split("-", "_")
        .filter { it.isNotBlank() }
        .joinToString(" ") { word ->
            when (word.lowercase()) {
                "atletico" -> "Atlético"
                "sao" -> "São"
                "gremio" -> "Grêmio"
                "vitoria" -> "Vitória"
                "america" -> "América"
                "union" -> "Unión"
                "lanus" -> "Lanús"
                "penarol" -> "Peñarol"
                "fc", "sc", "ac", "cr", "ec", "ca", "cd" -> word.uppercase()
                else -> word.replaceFirstChar { it.uppercase() }
            }
        }
}

fun deduplicateTeamSeasonGames(games: List<Game>): List<Game> {
    return games
        .map { g ->
            val canonHome = canonicalClubSlug(g.homeTeamId)
            val canonAway = canonicalClubSlug(g.awayTeamId)
            g.copy(
                homeTeamId = canonHome,
                awayTeamId = canonAway,
                teamIds = (g.teamIds.map { canonicalClubSlug(it) } + listOf(canonHome, canonAway)).distinct()
            )
        }
        .groupBy { g -> "${g.competitionId.lowercase()}|${g.date}|${g.homeTeamId}|${g.awayTeamId}" }
        .values
        .map { duplicates ->
            val best = duplicates.maxWithOrNull(
                compareBy<Game> { it.homeScore != null && it.awayScore != null }
                    .thenBy { it.channelsFor("BR").isNotEmpty() }
                    .thenBy { !it.id.contains("_vs_") }
            ) ?: duplicates.first()
            val mergedChannels = duplicates
                .fold(mutableMapOf<String, List<String>>()) { acc, item ->
                    item.channelsByCountry.forEach { (country, list) ->
                        val prev = acc[country].orEmpty()
                        acc[country] = (prev + list).distinct()
                    }
                    acc
                }
            if (mergedChannels.isNotEmpty() && best.channelsByCountry.isEmpty()) {
                best.copy(channelsByCountry = mergedChannels)
            } else {
                best
            }
        }
        .sortedWith(compareBy<Game> { it.date }.thenBy { it.kickoff })
}

private fun formatClubCompetitionRound(game: Game, strings: AppStrings? = null): String {
    val compLabel = when (game.competitionId.lowercase()) {
        "brasileirao" -> "Brasileirão"
        "libertadores" -> "Libertadores"
        "copadobrasil" -> "Copa do Brasil"
        "sulamericana" -> "Sul-Americana"
        else -> game.competitionId.replaceFirstChar { it.uppercase() }
    }
    val roundNum = game.matchday
        ?: game.stage?.takeIf { it.contains("regular", ignoreCase = true) }
            ?.substringAfterLast("-")?.trim()?.toIntOrNull()
    val roundLabel = when {
        roundNum != null -> strings?.formatRoundNumber(roundNum) ?: "${roundNum}ª Rodada"
        !game.stage.isNullOrBlank() && game.stage != "REGULAR_SEASON" -> game.stage
        else -> null
    }
    return if (roundLabel != null) "$compLabel · $roundLabel" else compLabel
}

/**
 * Next Match Card with link to game details and opponent club page.
 */
@Composable
fun ClubNextMatchCard(
    club: Club,
    nextGame: Game?,
    onNavigate: (String) -> Unit,
    strings: AppStrings? = null
) {
    Div({ style { padding(0.px, 16.px, 12.px) } }) {
        Span({
            style {
                display(DisplayStyle.Block)
                marginBottom(8.px)
                fontSize(11.px)
                fontWeight(800)
                fontFamily("Sora, sans-serif")
                color(Color("#16181D"))
                property("text-transform", "uppercase")
                letterSpacing(0.6.px)
            }
        }) {
            Text(strings?.nextMatchHeadline ?: "Próximo Confronto")
        }

        if (nextGame == null) {
            Div({
                style {
                    padding(24.px, 16.px)
                    backgroundColor(Color("#FFFFFF"))
                    border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                    borderRadius(16.px)
                    property("text-align", "center")
                    color(Color("#71717A"))
                    fontSize(13.px)
                }
            }) {
                Text(strings?.noGamesScheduled ?: "Nenhum jogo agendado")
            }
            return@Div
        }

        val homeClub = ClubCatalog.bySlug(nextGame.homeTeamId)
        val awayClub = ClubCatalog.bySlug(nextGame.awayTeamId)
        val isHomeCurrent = nextGame.homeTeamId == club.slug

        Div({
            onClick { onNavigate(WebRoutes.game(nextGame)) }
            style {
                backgroundColor(Color("#FFFFFF"))
                border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                property("border-left", "4px solid ${club.c1}")
                borderRadius(16.px)
                padding(16.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(10.px)
                cursor("pointer")
                property("box-shadow", "0 2px 8px rgba(22, 24, 29, 0.04)")
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.SpaceBetween)
                    fontSize(12.px)
                    fontFamily("Sora, sans-serif")
                    fontWeight(600)
                    color(Color("#71717A"))
                }
            }) {
                Span { Text(formatClubCompetitionRound(nextGame, strings)) }
                Span { Text("${formatBannerDate(nextGame.date, strings)} · ${extractKickoffTime(nextGame.kickoff)}") }
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.SpaceBetween)
                    padding(6.px, 0.px)
                }
            }) {
                // Home Club (Mandante)
                Div({
                    if (!isHomeCurrent && homeClub != null) {
                        onClick { event ->
                            event.stopPropagation()
                            onNavigate(WebRoutes.club(homeClub.slug))
                        }
                    }
                    style {
                        flex(1)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        gap(4.px)
                        if (!isHomeCurrent && homeClub != null) {
                            cursor("pointer")
                        }
                    }
                }) {
                    SafeImg(
                        src = homeClub?.logoUrl,
                        fallbackIcon = Icons.SHIELD,
                        fallbackSize = CrestSizes.HERO_CARD_FALLBACK,
                        modifier = {
                            width(CrestSizes.HERO_CARD_PX.px)
                            height(CrestSizes.HERO_CARD_PX.px)
                            property("width", CrestSizes.HERO_CARD_CSS)
                            property("height", CrestSizes.HERO_CARD_CSS)
                            property("object-fit", "contain")
                        }
                    )
                    Span({
                        style {
                            fontFamily("Sora, sans-serif")
                            fontSize(14.px)
                            fontWeight(800)
                            color(Color("#16181D"))
                        }
                    }) {
                        Text(formatTeamDisplayName(nextGame.homeTeamId))
                    }
                }

                // VS badge
                Div({
                    style {
                        padding(6.px, 12.px)
                        backgroundColor(Color("#F4F4F5"))
                        borderRadius(8.px)
                        fontSize(13.px)
                        fontWeight(800)
                        fontFamily("Sora, sans-serif")
                        color(Color("#16181D"))
                    }
                }) {
                    Text("VS")
                }

                // Away Club (Visitante)
                Div({
                    if (isHomeCurrent && awayClub != null) {
                        onClick { event ->
                            event.stopPropagation()
                            onNavigate(WebRoutes.club(awayClub.slug))
                        }
                    }
                    style {
                        flex(1)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        gap(4.px)
                        if (isHomeCurrent && awayClub != null) {
                            cursor("pointer")
                        }
                    }
                }) {
                    SafeImg(
                        src = awayClub?.logoUrl,
                        fallbackIcon = Icons.SHIELD,
                        fallbackSize = CrestSizes.HERO_CARD_FALLBACK,
                        modifier = {
                            width(CrestSizes.HERO_CARD_PX.px)
                            height(CrestSizes.HERO_CARD_PX.px)
                            property("width", CrestSizes.HERO_CARD_CSS)
                            property("height", CrestSizes.HERO_CARD_CSS)
                            property("object-fit", "contain")
                        }
                    )
                    Span({
                        style {
                            fontFamily("Sora, sans-serif")
                            fontSize(14.px)
                            fontWeight(800)
                            color(Color("#16181D"))
                        }
                    }) {
                        Text(formatTeamDisplayName(nextGame.awayTeamId))
                    }
                }
            }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(6.px)
                    paddingTop(6.px)
                    property("border-top", "1px solid #F4F4F5")
                }
            }) {
                ChannelBadgesRow(nextGame.channelsFor("BR"))
            }
        }
    }
}

/**
 * Recent matches list with badges and direct navigation to opponent club.
 */
@Composable
fun ClubRecentResultsList(
    club: Club,
    matches: List<Game>,
    onNavigate: (String) -> Unit,
    strings: AppStrings
) {
    Div({ style { padding(8.px, 16.px, 16.px) } }) {
        Span({
            style {
                display(DisplayStyle.Block)
                marginBottom(8.px)
                fontSize(11.px)
                fontWeight(800)
                fontFamily("Sora, sans-serif")
                color(Color("#16181D"))
                property("text-transform", "uppercase")
                letterSpacing(0.6.px)
            }
        }) {
            Text(strings.recentResults)
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(8.px)
            }
        }) {
            if (matches.isEmpty()) {
                Div({
                    style {
                        padding(24.px, 16.px)
                        backgroundColor(Color("#FFFFFF"))
                        border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                        borderRadius(12.px)
                        property("text-align", "center")
                        color(Color("#71717A"))
                        fontSize(13.px)
                    }
                }) {
                    Text(strings.noRecentResults)
                }
            } else {
                matches.forEach { match ->
                    val oppMatchId = if (match.homeTeamId == club.slug) match.awayTeamId else match.homeTeamId
                    val oppMatchClub = ClubCatalog.bySlug(oppMatchId)
                    val badgeText = resolveMatchResultBadge(match, club.slug, strings)
                    val (bgBadge, fgBadge) = resolveResultBadgeColor(badgeText, strings)

                    Div({
                        onClick { onNavigate(WebRoutes.game(match)) }
                        style {
                            backgroundColor(Color("#FFFFFF"))
                            border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                            borderRadius(12.px)
                            padding(12.px, 14.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.SpaceBetween)
                            cursor("pointer")
                            property("transition", "background-color 0.15s ease")
                        }
                    }) {
                        Div({
                            if (oppMatchClub != null) {
                                onClick { event ->
                                    event.stopPropagation()
                                    onNavigate(WebRoutes.club(oppMatchClub.slug))
                                }
                            }
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                gap(10.px)
                                cursor("pointer")
                            }
                        }) {
                            SafeImg(
                                src = oppMatchClub?.logoUrl,
                                fallbackIcon = Icons.SHIELD,
                                fallbackSize = CrestSizes.MATCH_CARD_FALLBACK,
                                modifier = {
                                    width(CrestSizes.MATCH_CARD_PX.px)
                                    height(CrestSizes.MATCH_CARD_PX.px)
                                    property("width", CrestSizes.MATCH_CARD_CSS)
                                    property("height", CrestSizes.MATCH_CARD_CSS)
                                    property("object-fit", "contain")
                                }
                            )
                            Span({
                                style {
                                    fontFamily("Sora, sans-serif")
                                    fontSize(13.px)
                                    fontWeight(700)
                                    color(Color("#16181D"))
                                }
                            }) {
                                Text(formatTeamDisplayName(oppMatchId))
                            }
                            Span({
                                style {
                                    fontSize(11.px)
                                    fontFamily("Sora, sans-serif")
                                    color(Color("#71717A"))
                                }
                            }) {
                                Text(formatBannerDate(match.date, strings))
                            }
                        }

                        Span({
                            style {
                                fontSize(11.px)
                                fontWeight(800)
                                fontFamily("Sora, sans-serif")
                                backgroundColor(Color(bgBadge))
                                color(Color(fgBadge))
                                padding(3.px, 8.px)
                                borderRadius(6.px)
                            }
                        }) {
                            Text(badgeText)
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun ClubHubSkeleton() {
    Div({
        style {
            padding(4.px, 16.px, 20.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
        }
    }) {
        Div({
            style {
                backgroundColor(Color("#FFFFFF"))
                border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                borderRadius(16.px)
                padding(16.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(12.px)
            }
        }) {
            Div({
                classes("fg-skeleton-shimmer")
                style { width(140.px); height(14.px); borderRadius(4.px) }
            })
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                }
            }) {
                Div({
                    classes("fg-skeleton-shimmer")
                    style { width(72.px); height(56.px); borderRadius(12.px) }
                })
                Div({
                    classes("fg-skeleton-shimmer")
                    style { width(40.px); height(28.px); borderRadius(8.px) }
                })
                Div({
                    classes("fg-skeleton-shimmer")
                    style { width(72.px); height(56.px); borderRadius(12.px) }
                })
            }
        }
        repeat(3) {
            Div({
                style {
                    backgroundColor(Color("#FFFFFF"))
                    border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                    borderRadius(12.px)
                    padding(14.px)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                }
            }) {
                Div({
                    classes("fg-skeleton-shimmer")
                    style { width(160.px); height(18.px); borderRadius(4.px) }
                })
                Div({
                    classes("fg-skeleton-shimmer")
                    style { width(48.px); height(20.px); borderRadius(6.px) }
                })
            }
        }
    }
}
